import React, { useState, useEffect } from 'react';
import { FirmAdminLayout } from './FirmAdminLayout';
import {
  FileText,
  Plus,
  Search,
  Filter,
  Calendar,
  Clock,
  User,
  AlertCircle,
  CheckCircle,
  MoreVertical,
  Flag,
  Edit,
  Trash2,
  Play,
  Pause,
  Square,
  Eye,
  Users,
  RefreshCw,
  Download,
  Upload,
  BarChart3,
  Activity,
  Copy,
  ArrowRight
} from 'lucide-react';
import { useAuthStore, api } from '../auth/authStore';
import toast from 'react-hot-toast';

// Import all components
import { TaskModal } from './TaskModal';
import { TaskStatusModal } from './TaskStatusModal';
import { TaskDetailsModal } from './TaskDetailsModal';
import { TimeTrackingModal } from './TimeTrackingModal';
import { AdvancedFiltersModal, FilterState } from './AdvancedFiltersModal';
import { TaskAnalytics } from './TaskAnalytics';
import { ActivityLog } from './ActivityLog';
import { TaskTemplatesManager } from './TaskTemplatesManager';
import { TaskReallocationModal } from './TaskReallocationModal';
import { TimeTrackerWidget } from '../../components/TimeTracker';
import { useTimeTracker } from '../../hooks/useTimeTracker';
import { RecurrenceBadge } from '../../components/ui/RecurrenceBadge';
import { TaskImportModal, TaskImportResultsModal } from './TaskImportModals';

interface Task {
  id: string;
  type: string;
  title: string;
  description?: string;
  clientId: string;
  clientName?: string;
  status: 'DRAFT' | 'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'COMPLETED' | 'SUBMITTED';
  assignees: {
    preparerId?: string;
    reviewerId?: string;
    submitterId?: string;
  };
  assigneeNames?: {
    preparer?: string;
    reviewer?: string;
    submitter?: string;
  };
  schedule?: {
    frequency: string;
    dueDate: string;
    periodStart?: string;
    periodEnd?: string;
  };
  checklist?: Array<{
    _id: string;
    label: string;
    done: boolean;
    required: boolean;
    order: number;
  }>;
  timeSpentMinutes: number;
  billing?: {
    feeType: string;
    fixedAmount?: number;
    billableRate?: number;
    estimatedHours?: number;
  };
  priority: 'low' | 'medium' | 'high' | 'urgent';
  tags?: string[];
  completionPercentage: number;
  createdAt: string;
  updatedAt: string;
  recurrenceInfo?: {
    isRecurring: boolean;
    description: string;
    nextDueDates: string[];
  };
}

interface TaskStats {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  byPriority: Record<string, number>;
  overdue: number;
}

interface TaskListResponse {
  tasks: Task[];
  total: number;
  page: number;
  limit: number;
}

export const TaskManagement: React.FC = () => {
  const { user } = useAuthStore();
  const timeTracker = useTimeTracker(user?.id);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [taskStats, setTaskStats] = useState<TaskStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    status: 'all',
    priority: 'all',
    type: 'all',
    assigneeId: 'all',
    clientId: 'all',
    dueDateFrom: '',
    dueDateTo: '',
    createdDateFrom: '',
    createdDateTo: '',
    tags: [],
    overdue: false,
    unassigned: false,
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTaskForStatus, setSelectedTaskForStatus] = useState<Task | null>(null);
  const [selectedTaskIdForDetails, setSelectedTaskIdForDetails] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const [showTimeTrackingModal, setShowTimeTrackingModal] = useState(false);
  const [selectedTaskForTimeTracking, setSelectedTaskForTimeTracking] = useState<{ id: string; title: string } | null>(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [selectedTaskForActivity, setSelectedTaskForActivity] = useState<string | null>(null);
  const [showTemplatesManager, setShowTemplatesManager] = useState(false);
  const [showReallocationModal, setShowReallocationModal] = useState(false);
  const [selectedTaskForReallocation, setSelectedTaskForReallocation] = useState<Task | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportResultsModal, setShowImportResultsModal] = useState(false);
  const [importResults, setImportResults] = useState<{
    successful: any[];
    failed: Array<{ task: any; error: string }>;
    total: number;
  } | null>(null);

  useEffect(() => {
    fetchTasks();
    fetchTaskStats();
  }, [currentPage, filters]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setDropdownOpen(null);
    };

    if (dropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [dropdownOpen]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(filters.search && { search: filters.search }),
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.priority !== 'all' && { priority: filters.priority }),
        ...(filters.type !== 'all' && { type: filters.type }),
        ...(filters.assigneeId !== 'all' && { assigneeId: filters.assigneeId }),
        ...(filters.clientId !== 'all' && { clientId: filters.clientId }),
        ...(filters.dueDateFrom && { dueAfter: filters.dueDateFrom }),
        ...(filters.dueDateTo && { dueBefore: filters.dueDateTo }),
        sortBy: 'schedule.dueDate',
        sortOrder: 'asc'
      });

      const response = await api.get(`/firm-admin/tasks?${params}`);

      if (response.data.success) {
        const data: TaskListResponse = response.data.data;
        setTasks(data.tasks);
        setTotalPages(Math.ceil(data.total / data.limit));
      } else {
        throw new Error('Failed to fetch tasks');
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setError('Failed to load tasks');
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchTaskStats = async () => {
    try {
      const response = await api.get('/firm-admin/tasks/stats');
      if (response.data.success) {
        setTaskStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching task stats:', error);
    }
  };

  const handleCreateTask = () => {
    setShowCreateModal(true);
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setShowEditModal(true);
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!confirm('Are you sure you want to delete this task?')) return;

    try {
      const response = await api.delete(`/firm-admin/tasks/${taskId}`);
      if (response.data.success) {
        toast.success('Task deleted successfully');
        fetchTasks();
        fetchTaskStats();
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const handleImportTasks = async (tasks: any[], file?: File) => {
    try {
      // If file is provided, use the bulk import API endpoint
      if (file) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await api.post('/tasks/import/bulk', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.data.success) {
          const data = response.data.data;
          const successful = data.createdTasks || [];
          const failed = (data.errors || []).map((err: any) => ({
            task: { rowNumber: err.rowNumber },
            error: err.error || 'Unknown error',
          }));

          setShowImportModal(false);
          setImportResults({
            successful,
            failed,
            total: data.totalRows,
          });
          setShowImportResultsModal(true);
          fetchTasks();
          fetchTaskStats();
        }
      } else {
        // Fallback: Process tasks individually (legacy behavior)
        const results = await Promise.allSettled(
          tasks.map(task => {
            // Transform the import data to match the API format
            const taskData: any = {
              type: task.type,
              title: task.title,
              description: task.description,
              clientId: task.clientId,
              priority: task.priority || 'medium',
              schedule: {
                frequency: task.frequency,
              },
              assignees: {},
            };

            if (task.preparerId) {
              taskData.assignees.preparerId = task.preparerId;
            }
            if (task.reviewerId) {
              taskData.assignees.reviewerId = task.reviewerId;
            }
            if (task.submitterId) {
              taskData.assignees.submitterId = task.submitterId;
            }
            if (task.dueDate) {
              taskData.schedule.dueDate = task.dueDate;
            }
            if (task.feeType) {
              taskData.billing = {
                feeType: task.feeType,
                fixedAmount: task.fixedAmount,
                billableRate: task.billableRate,
              };
            }
            if (task.estimatedHours) {
              if (!taskData.billing) taskData.billing = {};
              taskData.billing.estimatedHours = task.estimatedHours;
            }
            if (task.tags) {
              taskData.tags = task.tags.split(',').map((t: string) => t.trim());
            }

            return api.post('/firm-admin/tasks', taskData);
          })
        );

        const successful: any[] = [];
        const failed: Array<{ task: any; error: string }> = [];

        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            successful.push(result.value);
          } else {
            // Extract user-friendly error message
            let errorMessage = 'Unknown error';
            const error = result.reason;

            // Try to extract error message from different response formats
            if (error?.response?.data?.error?.message) {
              errorMessage = error.response.data.error.message;
            } else if (error?.response?.data?.message) {
              errorMessage = error.response.data.message;
            } else if (error?.response?.data?.error) {
              errorMessage = typeof error.response.data.error === 'string'
                ? error.response.data.error
                : 'Unknown error';
            } else if (error?.response?.status) {
              const statusCode = error.response.status;
              switch (statusCode) {
                case 404:
                  errorMessage = 'Client not found';
                  break;
                case 400:
                  errorMessage = 'Invalid data provided';
                  break;
                case 422:
                  errorMessage = 'Missing required information';
                  break;
                case 500:
                  errorMessage = 'Server error occurred';
                  break;
                default:
                  errorMessage = `Request failed (Error ${statusCode})`;
              }
            } else if (error?.message) {
              errorMessage = error.message;
            }

            failed.push({
              task: tasks[index],
              error: errorMessage,
            });
          }
        });

        setShowImportModal(false);
        setImportResults({ successful, failed, total: tasks.length });
        setShowImportResultsModal(true);
        fetchTasks();
        fetchTaskStats();
      }
    } catch (error: any) {
      console.error('Error importing tasks:', error);
      toast.error(error?.response?.data?.message || 'Failed to import tasks');
    }
  };

  const handleStatusUpdate = async (taskId: string, newStatus: string) => {
    try {
      const response = await api.patch(`/firm-admin/tasks/${taskId}/status`, {
        status: newStatus
      });
      if (response.data.success) {
        toast.success('Task status updated successfully');
        fetchTasks();
        fetchTaskStats();
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update task status');
    }
  };

  const handleBulkAction = async (action: string, data?: any) => {
    if (selectedTasks.length === 0) {
      toast.error('Please select tasks first');
      return;
    }

    try {
      const response = await api.post('/firm-admin/tasks/bulk-action', {
        taskIds: selectedTasks,
        action,
        data
      });

      if (response.data.success) {
        const result = response.data.data;
        toast.success(`Bulk action completed: ${result.success} successful, ${result.failed} failed`);
        setSelectedTasks([]);
        fetchTasks();
        fetchTaskStats();
      }
    } catch (error) {
      console.error('Error performing bulk action:', error);
      toast.error('Failed to perform bulk action');
    }
  };

  const handleSelectTask = (taskId: string) => {
    setSelectedTasks(prev =>
      prev.includes(taskId)
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTasks.length === tasks.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(tasks.map(task => task.id));
    }
  };

  const handleViewDetails = (taskId: string) => {
    setSelectedTaskIdForDetails(taskId);
    setShowDetailsModal(true);
    setDropdownOpen(null);
  };

  const handleQuickStatusUpdate = (task: Task) => {
    setSelectedTaskForStatus(task);
    setShowStatusModal(true);
    setDropdownOpen(null);
  };

  const handleEditFromDetails = () => {
    if (selectedTaskIdForDetails) {
      const task = tasks.find(t => t.id === selectedTaskIdForDetails);
      if (task) {
        setEditingTask(task);
        setShowEditModal(true);
        setShowDetailsModal(false);
      }
    }
  };

  const handleTimeTracking = (task: Task) => {
    setSelectedTaskForTimeTracking({ id: task.id, title: task.title });
    setShowTimeTrackingModal(true);
    setDropdownOpen(null);
  };

  const handleApplyAdvancedFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleViewActivity = (taskId: string) => {
    setSelectedTaskForActivity(taskId);
    setShowActivityLog(true);
    setDropdownOpen(null);
  };

  const handleReallocateTask = (task: Task) => {
    setSelectedTaskForReallocation(task);
    setShowReallocationModal(true);
    setDropdownOpen(null);
  };

  const getAssigneeName = (task: Task): string => {
    if (task.assigneeNames?.preparer) return task.assigneeNames.preparer;
    if (task.assigneeNames?.reviewer) return task.assigneeNames.reviewer;
    if (task.assigneeNames?.submitter) return task.assigneeNames.submitter;
    return 'Unassigned';
  };

  const getTimeProgress = (task: Task): { actual: number; estimated: number; percentage: number } => {
    const actualHours = Math.round((task.timeSpentMinutes / 60) * 10) / 10;
    const estimatedHours = task.billing?.estimatedHours || 0;
    const percentage = estimatedHours > 0 ? Math.min((actualHours / estimatedHours) * 100, 100) : 0;
    return { actual: actualHours, estimated: estimatedHours, percentage };
  };

  const isOverdue = (task: Task): boolean => {
    return !!(task.schedule?.dueDate && new Date(task.schedule.dueDate) < new Date() &&
           !['COMPLETED', 'SUBMITTED'].includes(task.status));
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-indigo-100 text-indigo-800',
      in_review: 'bg-yellow-100 text-yellow-800',
      ready_to_submit: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      submitted: 'bg-emerald-100 text-emerald-800',
      blocked: 'bg-red-100 text-red-800'
    };
    return styles[status as keyof typeof styles] || styles.draft;
  };

  const getPriorityBadge = (priority: string) => {
    const styles = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return styles[priority as keyof typeof styles] || styles.medium;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
      case 'submitted':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'in_progress':
        return <Play className="w-4 h-4 text-indigo-500" />;
      case 'in_review':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'ready_to_submit':
        return <Clock className="w-4 h-4 text-blue-500" />;
      case 'blocked':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'draft':
      default:
        return <FileText className="w-4 h-4 text-gray-500" />;
    }
  };

  const getPriorityIcon = (priority: string) => {
    const colors = {
      low: 'text-green-500',
      medium: 'text-yellow-500',
      high: 'text-orange-500',
      urgent: 'text-red-500'
    };
    return <Flag className={`w-4 h-4 ${colors[priority as keyof typeof colors] || colors.medium}`} />;
  };

  const displayStats = taskStats ? {
    total: taskStats.total,
    todo: taskStats.byStatus.draft || 0,
    inProgress: taskStats.byStatus.in_progress || 0,
    completed: (taskStats.byStatus.completed || 0) + (taskStats.byStatus.submitted || 0),
    overdue: taskStats.overdue
  } : {
    total: 0,
    todo: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0
  };

  if (loading && tasks.length === 0) {
    return (
      <FirmAdminLayout>
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 text-primary-500 animate-spin" />
            <span className="ml-2 text-gray-600">Loading tasks...</span>
          </div>
        </div>
      </FirmAdminLayout>
    );
  }

  if (error && tasks.length === 0) {
    return (
      <FirmAdminLayout>
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={fetchTasks}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </FirmAdminLayout>
    );
  }

  return (
    <FirmAdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Task Management</h1>
              <p className="text-gray-600">Organize, assign, and track tasks across your firm</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowTemplatesManager(true)}
                className="flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Copy className="w-4 h-4 mr-2" />
                Templates
              </button>
              <button
                onClick={() => setShowAnalytics(true)}
                className="flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </button>
              <button
                onClick={fetchTasks}
                className="flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center px-3 py-2 text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import
              </button>
              <button
                onClick={handleCreateTask}
                className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Task
              </button>
            </div>
          </div>

          {/* Time Tracker Widget */}
          <div className="mb-4">
            <TimeTrackerWidget
              userRole={user?.roles?.[0] || 'Staff'}
              availableTasks={tasks.map(task => ({
                id: task.id,
                title: task.title,
                status: task.status
              }))}
              onTimerUpdate={(timer) => {
                // Optionally refresh task stats when timer updates
                if (timer) {
                  fetchTaskStats();
                }
              }}
              className="max-w-md"
            />
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Tasks</p>
                <p className="text-2xl font-bold text-gray-900">{displayStats.total}</p>
              </div>
              <FileText className="w-8 h-8 text-gray-500" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">To Do</p>
                <p className="text-2xl font-bold text-gray-900">{displayStats.todo}</p>
              </div>
              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-blue-600">{displayStats.inProgress}</p>
              </div>
              <Play className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-green-600">{displayStats.completed}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{displayStats.overdue}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search tasks by title, client, or assignee..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-2">
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="DRAFT">Draft</option>
                <option value="TODO">To Do</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="REVIEW">Review</option>
                <option value="COMPLETED">Completed</option>
                <option value="SUBMITTED">Submitted</option>
              </select>

              <select
                value={filters.priority}
                onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Priority</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>

              <select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="TAX_RETURN">Tax Return</option>
                <option value="BOOKKEEPING">Bookkeeping</option>
                <option value="PAYROLL">Payroll</option>
                <option value="AUDIT">Audit</option>
                <option value="CONSULTATION">Consultation</option>
                <option value="OTHER">Other</option>
              </select>

              <button
                onClick={() => setShowAdvancedFilters(true)}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Filter className="w-4 h-4 mr-2" />
                Advanced
              </button>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedTasks.length > 0 && (
            <div className="flex items-center gap-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="text-sm text-blue-700">
                {selectedTasks.length} task{selectedTasks.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => handleBulkAction('updateStatus', { status: 'IN_PROGRESS' })}
                  className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Start
                </button>
                <button
                  onClick={() => handleBulkAction('updateStatus', { status: 'COMPLETED' })}
                  className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Complete
                </button>
                <button
                  onClick={() => handleBulkAction('updatePriority', { priority: 'high' })}
                  className="px-3 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700"
                >
                  High Priority
                </button>
                <button
                  onClick={() => handleBulkAction('delete')}
                  className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Delete
                </button>
                <button
                  onClick={() => setSelectedTasks([])}
                  className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Tasks Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedTasks.length === tasks.length && tasks.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Task
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assignee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tasks.map((task) => {
                  const timeProgress = getTimeProgress(task);
                  const assigneeName = getAssigneeName(task);
                  const overdue = isOverdue(task);

                  return (
                    <tr key={task.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedTasks.includes(task.id)}
                          onChange={() => handleSelectTask(task.id)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{task.title}</div>
                          {task.description && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">{task.description}</div>
                          )}
                          {task.tags && task.tags.length > 0 && (
                            <div className="flex gap-1 mt-1">
                              {task.tags.slice(0, 2).map((tag, index) => (
                                <span key={index} className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                                  {tag}
                                </span>
                              ))}
                              {task.tags.length > 2 && (
                                <span className="text-xs text-gray-500">+{task.tags.length - 2} more</span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-1">
                          <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            {task.type.replace('_', ' ')}
                          </span>
                          {task.recurrenceInfo && (
                            <div>
                              <RecurrenceBadge recurrenceInfo={task.recurrenceInfo} size="sm" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-primary-600" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{assigneeName}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{task.clientName || 'Unknown Client'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getPriorityIcon(task.priority)}
                          <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityBadge(task.priority)}`}>
                            {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(task.status)}
                          <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(task.status)}`}>
                            {task.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {task.schedule?.dueDate ? new Date(task.schedule.dueDate).toLocaleDateString() : 'No due date'}
                        </div>
                        {overdue && (
                          <div className="text-xs text-red-600 font-medium">Overdue</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {timeProgress.actual}h / {timeProgress.estimated}h
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                          <div
                            className={`h-1.5 rounded-full ${timeProgress.percentage > 100 ? 'bg-red-500' : 'bg-primary-600'}`}
                            style={{ width: `${Math.min(timeProgress.percentage, 100)}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {task.completionPercentage}% complete
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleViewDetails(task.id)}
                            className="text-gray-400 hover:text-blue-600"
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditTask(task)}
                            className="text-gray-400 hover:text-blue-600"
                            title="Edit task"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          {timeTracker.activeTimer?.taskId === task.id ? (
                            <button
                              onClick={() => timeTracker.stopTimer()}
                              className="text-red-500 hover:text-red-700"
                              title="Stop timer"
                              disabled={timeTracker.loading}
                            >
                              <Square className="w-4 h-4 fill-current" />
                            </button>
                          ) : (
                            <button
                              onClick={() => timeTracker.startTimer(task.id, `Working on: ${task.title}`)}
                              className="text-green-500 hover:text-green-700"
                              title="Start timer"
                              disabled={timeTracker.loading || !!timeTracker.activeTimer}
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          <div className="relative">
                            <button
                              onClick={() => setDropdownOpen(dropdownOpen === task.id ? null : task.id)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                            {dropdownOpen === task.id && (
                              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                                <div className="py-1">
                                  <button
                                    onClick={() => handleQuickStatusUpdate(task)}
                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                  >
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    Update Status
                                  </button>
                                  <button
                                    onClick={() => handleTimeTracking(task)}
                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                  >
                                    <Clock className="w-4 h-4 mr-2" />
                                    Track Time
                                  </button>
                                  <button
                                    onClick={() => handleViewActivity(task.id)}
                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                  >
                                    <Activity className="w-4 h-4 mr-2" />
                                    View Activity
                                  </button>
                                  <button
                                    onClick={() => {
                                      // Handle start/pause task
                                      const newStatus = task.status === 'IN_PROGRESS' ? 'DRAFT' : 'IN_PROGRESS';
                                      handleStatusUpdate(task.id, newStatus);
                                      setDropdownOpen(null);
                                    }}
                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                  >
                                    {task.status === 'IN_PROGRESS' ? (
                                      <>
                                        <Pause className="w-4 h-4 mr-2" />
                                        Pause Task
                                      </>
                                    ) : (
                                      <>
                                        <Play className="w-4 h-4 mr-2" />
                                        Start Task
                                      </>
                                    )}
                                  </button>
                                  <button
                                    onClick={() => handleReallocateTask(task)}
                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                  >
                                    <ArrowRight className="w-4 h-4 mr-2" />
                                    Reallocate Task
                                  </button>
                                  <div className="border-t border-gray-100"></div>
                                  <button
                                    onClick={() => {
                                      handleDeleteTask(task.id);
                                      setDropdownOpen(null);
                                    }}
                                    className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete Task
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {tasks.length === 0 && !loading && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
            <p className="text-gray-500 mb-4">
              {filters.search || filters.status !== 'all' || filters.priority !== 'all' || filters.type !== 'all' || filters.assigneeId !== 'all' || filters.clientId !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by creating your first task.'
              }
            </p>
            <button
              onClick={handleCreateTask}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Task
            </button>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 bg-white border-t border-gray-200">
            <div className="flex items-center text-sm text-gray-500">
              Showing page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 text-sm border rounded ${
                        currentPage === page
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Loading overlay */}
        {loading && tasks.length > 0 && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
            <RefreshCw className="w-6 h-6 text-primary-500 animate-spin" />
          </div>
        )}

        {/* Task Creation Modal */}
        <TaskModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            fetchTasks();
            fetchTaskStats();
          }}
          mode="create"
        />

        {/* Task Edit Modal */}
        <TaskModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingTask(null);
          }}
          onSuccess={() => {
            fetchTasks();
            fetchTaskStats();
          }}
          task={editingTask as any}
          mode="edit"
        />

        {/* Task Status Modal */}
        <TaskStatusModal
          isOpen={showStatusModal}
          onClose={() => {
            setShowStatusModal(false);
            setSelectedTaskForStatus(null);
          }}
          onSuccess={() => {
            fetchTasks();
            fetchTaskStats();
          }}
          task={selectedTaskForStatus}
        />

        {/* Task Details Modal */}
        <TaskDetailsModal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedTaskIdForDetails(null);
          }}
          onEdit={handleEditFromDetails}
          taskId={selectedTaskIdForDetails}
        />

        {/* Time Tracking Modal */}
        <TimeTrackingModal
          isOpen={showTimeTrackingModal}
          onClose={() => {
            setShowTimeTrackingModal(false);
            setSelectedTaskForTimeTracking(null);
          }}
          onSuccess={() => {
            fetchTasks();
            fetchTaskStats();
          }}
          taskId={selectedTaskForTimeTracking?.id || null}
          taskTitle={selectedTaskForTimeTracking?.title}
        />

        {/* Advanced Filters Modal */}
        <AdvancedFiltersModal
          isOpen={showAdvancedFilters}
          onClose={() => setShowAdvancedFilters(false)}
          onApplyFilters={handleApplyAdvancedFilters}
          currentFilters={filters}
        />

        {/* Task Analytics Modal */}
        <TaskAnalytics
          isOpen={showAnalytics}
          onClose={() => setShowAnalytics(false)}
        />

        {/* Activity Log Modal */}
        <ActivityLog
          isOpen={showActivityLog}
          onClose={() => {
            setShowActivityLog(false);
            setSelectedTaskForActivity(null);
          }}
          taskId={selectedTaskForActivity}
          showAllTasks={false}
        />

        {/* Task Templates Manager */}
        <TaskTemplatesManager
          isOpen={showTemplatesManager}
          onClose={() => setShowTemplatesManager(false)}
          onCreateFromTemplate={(templateId, clientId) => {
            // Refresh tasks after creating from template
            fetchTasks();
            fetchTaskStats();
          }}
        />

        {/* Task Reallocation Modal */}
        <TaskReallocationModal
          isOpen={showReallocationModal}
          onClose={() => {
            setShowReallocationModal(false);
            setSelectedTaskForReallocation(null);
          }}
          onSuccess={() => {
            fetchTasks();
            fetchTaskStats();
          }}
          taskId={selectedTaskForReallocation?.id || null}
          taskTitle={selectedTaskForReallocation?.title}
          currentAssignees={selectedTaskForReallocation ? {
            preparer: selectedTaskForReallocation.assigneeNames?.preparer ? {
              id: selectedTaskForReallocation.assignees?.preparerId || '',
              name: selectedTaskForReallocation.assigneeNames.preparer
            } : undefined,
            reviewer: selectedTaskForReallocation.assigneeNames?.reviewer ? {
              id: selectedTaskForReallocation.assignees?.reviewerId || '',
              name: selectedTaskForReallocation.assigneeNames.reviewer
            } : undefined,
            submitter: selectedTaskForReallocation.assigneeNames?.submitter ? {
              id: selectedTaskForReallocation.assignees?.submitterId || '',
              name: selectedTaskForReallocation.assigneeNames.submitter
            } : undefined,
          } : undefined}
        />

        {/* Task Import Modal */}
        <TaskImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImport={handleImportTasks}
          isLoading={loading}
        />

        {/* Task Import Results Modal */}
        <TaskImportResultsModal
          isOpen={showImportResultsModal}
          onClose={() => setShowImportResultsModal(false)}
          results={importResults}
        />
      </div>
    </FirmAdminLayout>
  );
};
